// ─────────────────────────────────────────────
//  UpdateBanner — "this tab is running an old build; reload when you're ready".
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Sits in NORMAL FLOW at the top of the app, like PreviewBanner and
//  TestModeBanner, so it pushes the page down instead of covering a control.
//  A floating bar over an OR board hides exactly the row somebody is reaching
//  for.
//
//  ⚠️ IT NEVER RELOADS ON ITS OWN. The reload is a button, and that is a
//  clinical decision, not a stylistic one: an automatic refresh would discard a
//  half-typed assignment or close a modal mid-decision, at whatever moment the
//  five-minute poll happened to fire. See useAppVersion.
//
//  ⚠️ NOT ON NATIVE. In the Capacitor shell the bundle is packaged INSIDE the
//  installed app; deploying hosting does not change it, and `asset-manifest.json`
//  describes a build the phone is not running. Telling a phone user to "reload"
//  would be advice that cannot work.
//
//  ⚠️ IT IS INFORMATION, NOT AN ALARM. Amber is already the preview-mode
//  identity signal and red belongs to emergencies; a neutral raised surface with
//  a normal-weight sentence keeps the board's colour vocabulary intact. Alarm
//  colour is finite — spend it on alarms.
// ─────────────────────────────────────────────

import React from "react";

import { isNative } from "../native/platform";
import tokens from "../theme/tokens";
import { useAppVersion } from "../hooks/useAppVersion";

const { color } = tokens;

/**
 * @param {object} props
 *   @param {Function} [props.reload]      injected so a test never reloads jsdom
 *   @param {object}   [props.versionOpts] passed to useAppVersion; the seam that
 *     lets a test inject `fetchImpl`/`getRunning`. Without it the only reachable
 *     states in jsdom are "fetch failed, say nothing" — which is indistinguishable
 *     from the banner being broken, so the suite would pass with the logic gone.
 */
export default function UpdateBanner({ reload, versionOpts }) {
  // Hooks run before any early return — a conditional return above a hook is
  // how this codebase's mobile sign-out broke once already.
  const native = isNative();
  const { outdated, dismiss } = useAppVersion({ enabled: !native, ...versionOpts });

  if (native || !outdated) return null;

  const doReload = () => {
    if (typeof reload === "function") return reload();
    if (typeof window !== "undefined") window.location.reload();
    return undefined;
  };

  return (
    // role="status" + aria-live="polite" so a screen reader is TOLD, at the next
    // pause, rather than interrupted. This is the only live region this banner
    // owns; the buttons inside it are ordinary controls.
    <div style={s.bar} role="status" aria-live="polite">
      <span aria-hidden="true" style={{ fontSize: 15 }}>⟳</span>
      <span style={s.text}>
        A newer version of ATC has been released. Reload when you reach a safe
        point — nothing you are doing will be lost until you do.
      </span>
      <button type="button" style={s.reload} onClick={doReload}>
        Reload now
      </button>
      <button type="button" style={s.later} onClick={dismiss}>
        Not now
      </button>
    </div>
  );
}

const s = {
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    // Raised neutral surface, NOT amber or red — see the header.
    background: color.surface.raised,
    color: color.text.primary,
    borderBottom: `1px solid ${color.border.default}`,
    padding: "8px 16px",
    fontSize: 13,
    fontFamily: "system-ui",
  },
  text: { flex: 1, minWidth: 200, lineHeight: 1.4 },
  reload: {
    background: color.accent.primary,
    color: color.surface.base,
    border: "none",
    borderRadius: 6,
    // 44px min touch target: this can appear on a phone and on a wall tablet.
    minHeight: 44,
    padding: "0 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "system-ui",
  },
  later: {
    background: "transparent",
    color: color.text.secondary,
    border: `1px solid ${color.border.default}`,
    borderRadius: 6,
    minHeight: 44,
    padding: "0 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "system-ui",
  },
};
